import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import MagneticButton from "../components/MagneticButton";
import { ArrowLeft, Calendar, Clock, MapPin, Wine, Utensils, Sparkles, Instagram } from "lucide-react";
import { fetchEvent } from "../lib/api";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

export default function EventDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetchEvent(slug).then(setEv).catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-6 pt-40">
        <div>
          <h1 className="font-serif text-5xl mb-4">Event not found</h1>
          <Link to="/classes" className="text-[11px] uppercase tracking-[0.22em] editorial-underline">← Back to classes</Link>
        </div>
      </div>
    );
  }
  if (!ev) {
    return <div className="min-h-screen flex items-center justify-center pt-40 text-espresso/50 text-sm uppercase tracking-[0.22em]">Loading…</div>;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: ev.name,
    startDate: `${ev.date}T18:30`,
    eventStatus: ev.sold_out ? "https://schema.org/EventScheduled" : "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: { "@type": "Place", name: ev.venue, address: { "@type": "PostalAddress", addressLocality: "Tucson", addressRegion: "AZ" } },
    description: ev.short_description,
    offers: { "@type": "Offer", price: ev.price, priceCurrency: "USD", availability: ev.sold_out ? "https://schema.org/SoldOut" : "https://schema.org/InStock" },
    organizer: { "@type": "Organization", name: "Salumi Tucson", url: "https://salumitucson.com" },
  };

  return (
    <>
      <Helmet>
        <title>{ev.name} · {formatDateLong(ev.date)} — Salumi Tucson</title>
        <meta name="description" content={ev.short_description} />
        <link rel="canonical" href={`https://salumitucson.com/classes/${ev.slug}`} />
        <meta property="og:title" content={`${ev.name} — Salumi Tucson`} />
        <meta property="og:description" content={ev.short_description} />
        <meta property="og:type" content="event" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] bg-espresso overflow-hidden" data-testid="event-hero">
        <video
          src={ev.hero_video || "/media/videos/salumi-1.mp4"}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso/30 via-espresso/30 to-espresso/85" />
        <div className="absolute inset-0 light-leak opacity-60" />
        <div className="relative h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24 text-ivory">
          <Link to="/classes" className="text-[11px] uppercase tracking-[0.22em] text-ivory/70 hover:text-ivory inline-flex items-center gap-2 mb-10" data-testid="event-back">
            <ArrowLeft strokeWidth={1.25} className="size-4" /> Back to classes
          </Link>
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-ivory/60 mb-6">{ev.category}</p>
          </Reveal>
          <motion.h1
            initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-5xl md:text-8xl lg:text-9xl tracking-editorial leading-[0.9] max-w-5xl"
          >
            {ev.name}
          </motion.h1>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-wrap items-center gap-8 text-sm text-ivory/80">
              <span className="inline-flex items-center gap-2"><Calendar strokeWidth={1.25} className="size-4 text-terracotta" /> {formatDateLong(ev.date)}</span>
              <span className="inline-flex items-center gap-2"><Clock strokeWidth={1.25} className="size-4 text-terracotta" /> {ev.time}</span>
              <span className="inline-flex items-center gap-2"><MapPin strokeWidth={1.25} className="size-4 text-terracotta" /> {ev.venue}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BODY: split layout */}
      <section className="bg-ivory text-espresso py-24 md:py-32">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Sticky media */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 space-y-6">
              {ev.gallery && ev.gallery.length > 0 && (
                <video src={ev.gallery[0]} autoPlay loop muted playsInline className="w-full aspect-[4/5] object-cover" />
              )}
              <div className="border border-espresso/15 p-8 bg-sand/40">
                <p className="text-[10px] uppercase tracking-[0.3em] text-olive mb-3">Reserve a seat</p>
                <p className="font-serif text-5xl tracking-editorial mb-2">${ev.price.toFixed(0)}</p>
                <p className="text-sm text-espresso/70 mb-6">
                  {ev.sold_out ? (
                    <span className="text-terracotta uppercase tracking-[0.22em] text-[11px]">Sold out · Join waitlist</span>
                  ) : (
                    <span>{ev.seats_remaining} of {ev.total_capacity} seats remaining</span>
                  )}
                </p>
                <Link to="/private-bookings#contact" data-testid="event-reserve-cta">
                  <MagneticButton variant="solid" as="span" className="w-full !justify-center">
                    {ev.sold_out ? "Join the waitlist" : "Reserve your seat"}
                  </MagneticButton>
                </Link>
                {ev.instagram_reel && (
                  <a href={ev.instagram_reel} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-espresso/70 hover:text-terracotta">
                    <Instagram strokeWidth={1.25} className="size-4" /> Watch the reel
                  </a>
                )}
              </div>
            </div>
          </aside>

          {/* Long form */}
          <article className="lg:col-span-7 space-y-20">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-6">The evening</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-editorial leading-[1.02] mb-8">
                {ev.short_description}
              </h2>
              <p className="text-lg text-espresso/75 leading-relaxed">{ev.full_description}</p>
            </Reveal>

            {ev.what_youll_learn?.length > 0 && (
              <Reveal>
                <div className="flex items-center gap-3 mb-8">
                  <Sparkles strokeWidth={1.25} className="size-5 text-terracotta" />
                  <p className="text-[11px] uppercase tracking-[0.3em] text-olive">What you'll learn</p>
                </div>
                <ul className="divide-y divide-espresso/10 border-t border-espresso/15">
                  {ev.what_youll_learn.map((t, i) => (
                    <li key={i} className="py-5 flex gap-6 items-start">
                      <span className="font-serif italic text-terracotta text-xl">0{i + 1}</span>
                      <span className="font-serif text-2xl tracking-editorial flex-1">{t}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}

            {ev.menu_preview?.length > 0 && (
              <Reveal>
                <div className="flex items-center gap-3 mb-8">
                  <Utensils strokeWidth={1.25} className="size-5 text-terracotta" />
                  <p className="text-[11px] uppercase tracking-[0.3em] text-olive">Menu preview</p>
                </div>
                <ol className="space-y-4 text-lg">
                  {ev.menu_preview.map((m, i) => (
                    <li key={i} className="flex gap-5 border-b border-espresso/10 pb-4">
                      <span className="text-[11px] uppercase tracking-[0.3em] text-espresso/40 pt-1.5 w-10">{String(i + 1).padStart(2, "0")}</span>
                      <span className="flex-1 font-serif text-2xl tracking-editorial">{m}</span>
                    </li>
                  ))}
                </ol>
              </Reveal>
            )}

            {ev.cocktail_pairing && (
              <Reveal>
                <div className="border-l-2 border-terracotta pl-6 py-2">
                  <div className="flex items-center gap-3 mb-3">
                    <Wine strokeWidth={1.25} className="size-5 text-terracotta" />
                    <p className="text-[11px] uppercase tracking-[0.3em] text-olive">Pairings</p>
                  </div>
                  <p className="font-serif text-3xl italic tracking-editorial">{ev.cocktail_pairing}</p>
                </div>
              </Reveal>
            )}

            {ev.dietary_notes && (
              <Reveal>
                <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-3">Dietary notes</p>
                <p className="text-base text-espresso/75 leading-relaxed">{ev.dietary_notes}</p>
              </Reveal>
            )}

            {ev.faqs?.length > 0 && (
              <Reveal>
                <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-6">Questions</p>
                <Accordion type="single" collapsible className="border-t border-espresso/15">
                  {ev.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`q-${i}`} className="border-b border-espresso/10">
                      <AccordionTrigger className="font-serif text-2xl tracking-editorial text-left hover:no-underline py-6" data-testid={`faq-trigger-${i}`}>
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-base text-espresso/75 leading-relaxed pb-6">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}
          </article>
        </div>
      </section>
    </>
  );
}

function formatDateLong(d) {
  try {
    const date = new Date(d + "T00:00:00");
    return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}
